import { createSelector } from '@ngrx/store';
import { docsFeature } from '../docs/docs.reducer';
import { docKey } from '../docs/docs.normalize';
import { selectActiveProjectId } from '../workspace/workspace.selectors';
import { ChatMessage, DefineDefinition, hydrateDefinition, normalizeRepoContext, RepoContext } from './define.models';

const { selectEntities, selectKeyIndex, selectStatusByKey, selectErrorByKey } = docsFeature;

function dataFor(entities: ReturnType<typeof selectEntities>, keyIndex: Record<string, string>, projectId: string | null, key: string): any {
  if (!projectId) return null;
  const id = keyIndex[docKey(projectId, key)];
  return id ? entities.entities[id]?.data ?? null : null;
}

export const selectDefineDefinition = createSelector(
  selectActiveProjectId, selectEntities, selectKeyIndex,
  (pid, entities, keyIndex): DefineDefinition => hydrateDefinition(dataFor(entities, keyIndex, pid, 'define.definition'))
);

export const selectDefineChatMessages = createSelector(
  selectActiveProjectId, selectEntities, selectKeyIndex,
  (pid, entities, keyIndex): ChatMessage[] => {
    const data = dataFor(entities, keyIndex, pid, 'define.chat');
    return Array.isArray(data?.messages) ? (data.messages as ChatMessage[]) : [];
  }
);

export const selectDefineRepoContext = createSelector(
  selectActiveProjectId, selectEntities, selectKeyIndex,
  (pid, entities, keyIndex): RepoContext | null => normalizeRepoContext(dataFor(entities, keyIndex, pid, 'repos.settings'))
);

export const selectDefineLoading = createSelector(
  selectActiveProjectId, selectStatusByKey,
  (pid, statusByKey) => (pid ? statusByKey[docKey(pid, 'define.definition')] === 'loading' : false)
);

export const selectDefineError = createSelector(
  selectActiveProjectId, selectErrorByKey,
  (pid, errorByKey) => (pid ? errorByKey[docKey(pid, 'define.definition')] ?? null : null)
);

export const selectDefineIsReady = createSelector(
  selectDefineDefinition,
  (def) => Boolean((def.name ?? '').trim() && (def.description ?? '').trim())
);
